import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useFlyingCart } from '../contexts/FlyingCartContext';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import { useSound } from '../hooks/useSound';
import type { Product } from '../types/Product';
import LazyImage from './LazyImage';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isFuseAnimating, setIsFuseAnimating] = useState(false);
  const { addToCart, updateQuantity, getItemQuantity } = useCart();
  const { addToFavorites, removeFromFavorites, isFavorite } = useUser();
  const { showToast } = useToast();
  const { triggerFlyingAnimation } = useFlyingCart();
  const cartQuantity = getItemQuantity(product.id);
  const isInFavorites = isFavorite(product.id);
  const { play: playKissSound } = useSound('/sounds/kiss.mp3', { volume: 0.9 });

  const handleAddToCart = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (product.inStock) {
      setIsLoading(true);
      try {
        // Get button position for flying animation
        if (event && event.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect();
          const startPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
          triggerFlyingAnimation(startPosition, product.image);
        }
        
        await new Promise(resolve => setTimeout(resolve, 300));
        if (onAddToCart) {
          onAddToCart(product);
        } else {
          addToCart(product);
        }
        showToast(`${product.name} added to cart!`, 'success');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const decreaseQuantity = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (cartQuantity > 1) {
      updateQuantity(product.id, cartQuantity - 1);
    } else if (cartQuantity === 1) {
      updateQuantity(product.id, 0); // This will remove the item from cart
    }
  };

  const increaseQuantity = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (cartQuantity < 99) { // Max quantity limit
      // Trigger flying animation
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        const startPosition = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        triggerFlyingAnimation(startPosition, product.image);
      }
      
      updateQuantity(product.id, cartQuantity + 1);
    }
  };

  const handleToggleFavorite = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (isInFavorites) {
      removeFromFavorites(product.id);
      showToast(`${product.name} removed from favorites`, 'info');
    } else {
      addToFavorites(product);
      playKissSound(); // Play kiss sound when adding to favorites
      showToast(`${product.name} added to favorites!`, 'success');
    }
  };

  const handleProductClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent navigation when clicking on interactive elements
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    navigate(`/product/${product.slug}`);
  };

  const handleSoldOutHover = () => {
    if (!product.inStock) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
    }
  };

  const handleFuseMouseEnter = () => {
    if (product.inStock && cartQuantity === 0 && !isLoading) {
      setIsFuseAnimating(true);
    }
  };

  const handleFuseMouseLeave = () => {
    setIsFuseAnimating(false);
  };

  const CartIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  );

  const HeartIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

  const discountPercentage = product.originalPrice && product.originalPrice > product.price 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <>
      <style>{`
        @keyframes fuseBurn {
          0% { 
            width: 0%;
          }
          100% { 
            width: 100%;
          }
        }
      `}</style>
      <div 
        className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-neutral-900/10 transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] h-full flex flex-col border border-neutral-100 hover:border-accent-200 relative cursor-pointer ${
          !product.inStock ? 'cursor-not-allowed' : ''
        } ${
          isShaking ? 'animate-head-shake' : ''
        }`}
        onMouseEnter={handleSoldOutHover}
        onClick={handleProductClick}
      >
      {/* Image Section */}
      <div className="relative overflow-hidden">
        <div className="aspect-square bg-neutral-50 relative">
          <LazyImage 
            src={product.image} 
            alt={product.name} 
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:blur-[1px]"
          />
          
          {/* Shimmer Effect on Hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {/* Overlay Actions */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex gap-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <button 
                onClick={(e) => handleToggleFavorite(e)}
                className={`p-3 rounded-full shadow-lg hover:scale-125 transition-all duration-200 hover:shadow-xl hover:animate-bounce-soft ${
                  isInFavorites 
                    ? 'bg-accent-500 text-white hover:bg-accent-600' 
                    : 'bg-white text-neutral-900 hover:bg-accent-50 hover:text-accent-600'
                }`}
              >
                <HeartIcon />
              </button>
              <button 
                onClick={(e) => handleAddToCart(e)}
                disabled={!product.inStock || isLoading}
                className={`p-3 rounded-full shadow-lg hover:scale-125 transition-all duration-200 hover:shadow-xl ${
                  product.inStock 
                    ? 'bg-accent-600 text-white hover:bg-accent-700 hover:animate-pulse-glow' 
                    : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <CartIcon />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.tags?.includes("New") && (
            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 animate-fade-in-left">
              New
            </span>
          )}
          {product.featured && (
            <span className="bg-accent-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 animate-fade-in-left hover:animate-pulse-glow">
              Featured
            </span>
          )}
          {discountPercentage > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md hover:shadow-lg hover:scale-110 transition-all duration-300 animate-fade-in-left hover:animate-wiggle">
              -{discountPercentage}%
            </span>
          )}
        </div>

        {!product.inStock && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center animate-fade-in">
            <span className="bg-neutral-900 text-white text-sm font-bold px-4 py-2 rounded-full animate-fade-in-up">
              Sold Out
            </span>
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="p-6 flex-grow flex flex-col">
        {/* Product Name */}
        <h3 className="text-lg font-semibold text-neutral-900 mb-2 line-clamp-2 flex-grow">
          {product.name}
        </h3>
        
        {/* Category and Pieces per Box */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-neutral-500 capitalize">
            {product.category.replace('-', ' ')}
          </p>
          {product.piecesPerBox && product.piecesPerBox > 1 && (
            <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2 py-1 rounded-full sm:hidden">
              {product.piecesPerBox} pcs/box
            </span>
          )}
        </div>
        
        {/* Price Section */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl font-bold text-neutral-900">₹{product.price.toLocaleString()}</span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-sm text-neutral-400 line-through">₹{product.originalPrice.toLocaleString()}</span>
            )}
          </div>
          
          {/* Product Rating - Only show if product has ratings */}
          {product.rating && product.reviewCount && product.reviewCount > 0 ? (
            <div className="flex items-center gap-1">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className={`w-4 h-4 ${i < Math.floor(product.rating || 0) ? 'fill-current text-yellow-400' : 'fill-current text-gray-300'}`} viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
                  </svg>
                ))}
              </div>
              <span className="text-sm text-neutral-500 ml-1">({product.rating.toFixed(1)})</span>
            </div>
          ) : null}
        </div>
        
        {/* Add to Cart Controls */}
        {!product.inStock ? (
          <button 
            className="w-full py-3 px-6 rounded-xl font-semibold text-sm bg-neutral-200 text-neutral-500 cursor-not-allowed"
            disabled
          >
            Sold Out
          </button>
        ) : cartQuantity === 0 ? (
          /* Show Add to Cart button when item is not in cart */
          <button 
            className="w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 relative overflow-hidden group/btn bg-accent-600 text-white hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transform hover:scale-105 hover:-translate-y-1"
            onClick={(e) => handleAddToCart(e)}
            onMouseEnter={handleFuseMouseEnter}
            onMouseLeave={handleFuseMouseLeave}
            disabled={isLoading}
          >
            <span className="relative z-10 flex items-center gap-1">
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Adding...</span>
                </>
              ) : (
                <>
                  <span className="transition-transform duration-300 group-hover/btn:scale-110">
                    <CartIcon />
                  </span>
                  <span className="hidden sm:inline">Add to Cart</span>
                  <span className="sm:hidden">Add</span>
                </>
              )}
            </span>
            
            {/* TEST: Simple visible element first */}
            {isFuseAnimating && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
                {/* Test: Static red bar to confirm visibility */}
                <div className="absolute top-1/2 left-0 w-full h-3 bg-red-500 transform -translate-y-1/2"></div>
                
                {/* Fuse Line - Burns from left to right */}
                <div 
                  className="absolute top-1/2 left-0 h-3 bg-yellow-400 transform -translate-y-1/2"
                  style={{
                    width: '0%',
                    animation: 'fuseBurn 3s linear infinite'
                  }}
                />
              </div>
            )}
            
            <div className="absolute inset-0 bg-gradient-to-r from-accent-700 to-accent-500 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
          </button>
        ) : (
          /* Show quantity controls when item is in cart */
          <div className="flex items-center border border-neutral-200 rounded-xl overflow-hidden w-full">
            <button
              onClick={(e) => decreaseQuantity(e)}
              className="flex-1 h-12 flex items-center justify-center text-lg font-bold transition-all duration-200 bg-white text-neutral-700 hover:bg-neutral-50 hover:text-accent-600"
            >
              −
            </button>
            <div className="flex-1 h-12 flex items-center justify-center bg-white text-neutral-900 font-semibold text-sm border-x border-neutral-200">
              {cartQuantity}
            </div>
            <button
              onClick={(e) => increaseQuantity(e)}
              disabled={cartQuantity >= 99}
              className={`flex-1 h-12 flex items-center justify-center text-lg font-bold transition-all duration-200 ${
                cartQuantity >= 99 
                  ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' 
                  : 'bg-white text-neutral-700 hover:bg-neutral-50 hover:text-accent-600'
              }`}
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProductCard;